import {
  Component,
  DestroyRef,
  ElementRef,
  computed,
  inject,
  input,
  linkedSignal,
  signal,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { catchError, take } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { CmsContentType, CmsQueryList, CmsQueryListItem, CmsTaxonomyGeneral, CmsViewType } from '@fcom/core-api';
import { sanitizeWhiteListedPath } from '@fcom/core/utils/cms-path-helpers';
import { stopPropagation } from '@fcom/core/utils/event';
import { ConfigService, MonitoringLogger } from '@fcom/core';
import { ButtonTheme } from '@fcom/ui-components/components/buttons/button/enums';
import { IconPosition } from '@fcom/ui-components/components/icons/enums';
import { LoaderColor, LoaderTheme } from '@fcom/ui-components/components/loader/enums';

import { CmsTemplateService } from '../../../services/template/cms-template.service';
import { getFontLevel, getHeaderLevel } from '../../../services/cms-utils/cms-utils';

@Component({
  selector: 'fin-teaser-list',
  templateUrl: './teaser-list.component.html',
  styleUrls: ['./teaser-list.component.scss'],
  standalone: false,
})
export class TeaserListComponent {
  private router = inject(Router);
  private configService = inject(ConfigService);
  private cmsTemplateService = inject(CmsTemplateService);
  private monitoringLogger = inject(MonitoringLogger);
  private destroyRef = inject(DestroyRef);

  readonly DEFAULT_HEADER_LEVEL = 3;
  readonly TAXONOMY: typeof CmsTaxonomyGeneral = CmsTaxonomyGeneral;
  protected readonly LoaderTheme = LoaderTheme;
  protected readonly LoaderColor = LoaderColor;
  protected readonly ButtonTheme = ButtonTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly IconPosition = IconPosition;
  readonly CmsContentType = CmsContentType;

  readonly content = input.required<CmsQueryList>();

  readonly listItems = viewChildren('listItem', { read: ElementRef });

  private readonly path = sanitizeWhiteListedPath(this.router.url, this.configService.cfg);
  readonly loading = signal(false);
  readonly currentPage = signal(+this.router.parseUrl(this.path).queryParams.pageNum || 0);
  readonly items = linkedSignal<CmsQueryList, CmsQueryListItem[]>({
    source: this.content,
    computation: (content, previous) => {
      return previous?.value ?? content.items;
    },
  });

  readonly numberOfPages = computed(() => {
    return this.content().numberOfPages || 1;
  });

  readonly showLoadMore = computed(() => {
    return this.currentPage() < this.numberOfPages() - 1;
  });

  readonly nextPageLink = computed(() => {
    const urlWithoutPageNum = this.path.replace(/[?&]pageNum=\d/, '');
    const delimiter = urlWithoutPageNum.includes('?') ? '&' : '?';
    return `${urlWithoutPageNum}${delimiter}pageNum=${this.currentPage() + 1}`;
  });

  readonly headerLevel = computed(() => {
    return getHeaderLevel(this.content().subjectTaxonomyTags || []) ?? this.DEFAULT_HEADER_LEVEL;
  });

  readonly headerFontLevel = computed(() => {
    return getFontLevel(this.content().subjectTaxonomyTags || []) ?? 'font-headline-small';
  });

  readonly renderHeading = computed(() => {
    const tags = this.content().subjectTaxonomyTags;
    return (
      tags?.includes(this.TAXONOMY.TEXT_LINK_LEFT_LIST) ||
      tags?.includes(this.TAXONOMY.QUERY_TEASER_LIST_THIRD) ||
      tags?.includes(this.TAXONOMY.QUERY_LIST_NEWS_COLLECTION)
    );
  });

  loadNextItems(event: Event): boolean {
    if (this.loading()) {
      return false;
    }
    stopPropagation(event);
    this.loading.set(true);
    const nextPage = this.currentPage() + 1;
    this.currentPage.set(nextPage);
    const delimiter = this.path.includes('?') ? '&' : '?';
    const templatePath = `${this.path}${delimiter}pageNum=${nextPage}`;
    this.cmsTemplateService
      .load(templatePath)
      .pipe(
        take(1),
        catchError((error: unknown) => {
          this.monitoringLogger.error('Error loading additional query list items', { error, path: templatePath });
          this.loading.set(false);
          throw error;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((template) => {
        const currentItems = this.items();
        const firstNewItemIndex = currentItems.length;
        const newItems: CmsQueryListItem[] = template.main
          .filter((content) => content.viewType === CmsViewType.TEASER_LIST)
          ?.flatMap((content: CmsQueryList) => content.items);
        this.items.set(currentItems.concat(newItems));
        this.loading.set(false);
        // Set focus to first new element to improve a11y
        this.setFocus(firstNewItemIndex);
      });
    return false;
  }

  private setFocus(index: number): void {
    setTimeout(() => {
      this.listItems()[index]?.nativeElement?.querySelector('a')?.focus();
    });
  }
}
